import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { ArrowLeft, Save, FileText, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../../hooks/useApi';
import { useAuth } from '../../../contexts/AuthContext';

const labelClassName = 'text-sm font-medium text-foreground';
const fieldClassName =
  'h-11 rounded-lg border-1 border-muted-foreground/20 bg-background text-foreground shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:border-primary/50';
const selectTriggerClassName =
  'h-11 rounded-lg border-1 border-muted-foreground/20 bg-background text-foreground shadow-sm focus:ring-2 focus:ring-primary/25 focus:border-primary/50';

export default function AddQuote() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [isLoadingDeviceTypes, setIsLoadingDeviceTypes] = useState(false);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [items, setItems] = useState([
    { device_type_id: '', quantity: '' }
  ]);
  const { post, get } = useApi();
  
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      account_id: '',
      notes: ''
    }
  });

  // Fetch device types and accounts on component mount
  useEffect(() => {
    fetchDeviceTypes();
    fetchAccounts();
  }, [user]);

  // Set the first account as default when accounts are loaded
  useEffect(() => {
    if (accounts.length > 0 && !watch('account_id')) {
      setValue('account_id', accounts[0]._id);
    }
  }, [accounts, setValue]);

  const fetchDeviceTypes = async () => {
    setIsLoadingDeviceTypes(true);
    try {
      const response = await get('/device-types');
      
      
      if (response?.success && Array.isArray(response?.data)) {
        setDeviceTypes(response.data);
      } else {
        setDeviceTypes([]);
      }
    } catch (error) {
      toast.error('Failed to fetch device types');
      setDeviceTypes([]);
    } finally {
      setIsLoadingDeviceTypes(false);
    }
  };

  const fetchAccounts = async () => {
    setIsLoadingAccounts(true);
    try {
      const response = await get('/accounts');
      
      
      // Filter accounts to show only the logged-in user's account
      let accountsData = [];
      if (response?.success && Array.isArray(response?.data)) {
        accountsData = response.data;
      } else {
        accountsData = [];
      }
      
      // Don't filter - show ALL accounts for admin
      
      // Filter to show only the current user's account
      const currentUserAccount = accountsData.filter(account => 
        account.email === user?.email || account._id === user?._id
      );
      
      
      // If no filtered accounts, show all accounts (for admin users)
      const finalAccounts = currentUserAccount.length > 0 ? currentUserAccount : accountsData;
      
      setAccounts(finalAccounts);
    } catch (error) {
      toast.error('Failed to fetch accounts');
      setAccounts([]);
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  const handleAddItem = () => {
    setItems([...items, { device_type_id: '', quantity: '' }]);
  };

  const handleRemoveItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  // Get device type price
  const getDeviceTypePrice = (deviceTypeId) => {
    const deviceType = deviceTypes.find(dt => dt._id === deviceTypeId);
    return deviceType ? parseFloat(deviceType.price) : 0;
  };

  // Calculate item amount
  const calculateItemAmount = (item) => {
    const price = getDeviceTypePrice(item.device_type_id);
    const quantity = parseInt(item.quantity) || 0;
    return price * quantity;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Calculate total amount for all items
  const calculateTotalAmount = (items) => {
    return items.reduce((total, item) => {
      return total + calculateItemAmount(item);
    }, 0);
  };

  const onSubmit = async (data) => {
    
    // Validate items
    const validItems = items.filter(item => 
      item.device_type_id && item.quantity
    );


    if (validItems.length === 0) {
      toast.error('Please add at least one item with device type and quantity');
      return;
    }

    if (!data.account_id) {
      toast.error('Please select an account');
      return;
    }

    setIsLoading(true);
    try {
      
      const payload = {
        account_id: data.account_id,
        notes: data.notes,
        total_sum: calculateTotalAmount(validItems).toString(),
        items: validItems.map(item => ({
          device_type_id: item.device_type_id,
          quantity: parseInt(item.quantity),
          unit_price: getDeviceTypePrice(item.device_type_id),
          total_price: calculateItemAmount(item)
        }))
      };

      
      const response = await post('/admin/quotes', payload);
      
      
      if (response.data) {
        toast.success('Quote created successfully!');
        navigate('/admin/quotes');
      }
    } catch (error) {
      
      // Display detailed error message
      let errorMessage = 'Failed to create quote';
      
      if (error.response?.data?.errors) {
        // If there are validation errors, show them
        const errors = error.response.data.errors;
        errorMessage = errors.map(err => err.msg).join(', ');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl bg-background text-foreground min-h-screen">
      <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold">Create Quote</h1>
          <p className="text-muted-foreground">Generate a new quotation for devices</p>
        </div>
       
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Quote Information */}
        <Card className="border-1 border-muted-foreground/20 bg-card shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <FileText className="w-5 h-5" />
              Quote Information
            </CardTitle>
            <CardDescription>
            Basic quote details and account information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Account Selection */}
              <div className="space-y-2">
                <Label htmlFor="account_id" className={labelClassName}>Account *</Label>
                <Select
                  onValueChange={(value) => {
                    setValue('account_id', value);
                  }}
                  value={watch('account_id') || ''}
                >
                  <SelectTrigger className={`w-full ${selectTriggerClassName}`}>
                    <SelectValue placeholder={accounts.length > 0 ? "Select account" : "No accounts available"} />
                  </SelectTrigger>
                  <SelectContent className='border-0 '>
                    {accounts.map((account) => (
                      <SelectItem key={account._id} value={account._id}>
                        {account.name} ({account.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.account_id && (
                  <p className="text-sm text-red-500">{errors.account_id.message}</p>
                )}
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes" className={labelClassName}>Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Enter quote notes..."
                  className="min-h-[100px] rounded-lg border-1 border-muted-foreground/20 bg-background text-foreground shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:border-primary/50"
                  {...register('notes', {
                    required: false,
                  })}
                />
                {errors.notes && (
                  <p className="text-sm text-red-500">{errors.notes.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quote Items */}
        <Card className="border-1 border-muted-foreground/20 bg-card shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <FileText className="w-5 h-5" />
              Quote Items
            </CardTitle>
            <CardDescription>
              Add device types and quantities for this quote
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {items.map((item, index) => (
              <div key={index} className="p-4 bg-background/50 rounded-lg space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-sm">Item {index + 1}</h4>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleAddItem}
                      className=""
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                    {items.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveItem(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Device Type Selection */}
                  <div className="space-y-2">
                    <Label className={labelClassName}>Device Type *</Label>
                    <Select
                      value={item.device_type_id}
                      onValueChange={(value) => handleItemChange(index, 'device_type_id', value)}
                      disabled={isLoadingDeviceTypes}
                    >
                      <SelectTrigger className={`w-full ${selectTriggerClassName}`}>
                        <SelectValue placeholder={isLoadingDeviceTypes ? "Loading..." : "Select device type"} />
                      </SelectTrigger>
                      <SelectContent className='border-0'>
                        {deviceTypes.map((deviceType) => (
                          <SelectItem key={deviceType._id} value={deviceType._id}>
                            {deviceType.name} - {deviceType.manufacturer}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Quantity */}
                  <div className="space-y-2">
                    <Label className={labelClassName}>Quantity *</Label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="Enter quantity"
                      className={fieldClassName}
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    />
                  </div>

                  {/* Price and Amount */}
                  <div className="space-y-2">
                    <Label className={labelClassName}>Price & Amount</Label>
                    <div className="space-y-1">
                      <div className="text-sm text-muted-foreground">
                        Price: {item.device_type_id ? formatCurrency(getDeviceTypePrice(item.device_type_id)) : '₹0.00'}
                      </div>
                      <div className="text-sm font-medium text-foreground">
                        Amount: {formatCurrency(calculateItemAmount(item))}
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            ))}


          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            disabled={isLoading}
            className='border-muted-foreground/10 border-1'
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="outline"
            disabled={isLoading || isLoadingDeviceTypes || isLoadingAccounts}
            className="flex items-center gap-2 border-muted-foreground/10 border-1"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Create Quote
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}